import React, {
  createContext,
  useState,
  useCallback,
  useEffect,
} from 'react';

import {
  getProfile,
  getRole,
  logoutUser,
} from '../api/general/api';

export const AuthContext = createContext();

function normIsAdmin(raw) {
  if (raw === true) return true;
  if (!raw) return false;
  if (typeof raw === 'number') return raw === 1;
  if (typeof raw === 'string') {
    const v = raw.trim().toLowerCase();
    return v === '1' || v === 'true' || v === 'yes' || v === 'admin';
  }
  if (typeof raw === 'object') {
    const u = raw;
    if (u.is_admin === true || u.isAdmin === true) return true;
    if (typeof u.role === 'string' && ['admin', 'owner', 'superadmin'].includes(u.role)) return true;
    if (Array.isArray(u.roles) && u.roles.includes('admin')) return true;
    if (Array.isArray(u.scopes) && u.scopes.includes('admin')) return true;
    if (typeof u.is_admin === 'number') return u.is_admin === 1;
    if (typeof u.isAdmin === 'number') return u.isAdmin === 1;
    if (typeof u.is_admin === 'string') {
      const v = u.is_admin.trim().toLowerCase();
      if (['1', 'true', 'yes', 'admin'].includes(v)) return true;
    }
    if (typeof u.isAdmin === 'string') {
      const v = u.isAdmin.trim().toLowerCase();
      if (['1', 'true', 'yes', 'admin'].includes(v)) return true;
    }
  }
  return false;
}

const AuthProvider = ({ children }) => {
  /* ───── state ───── */
  const [auth,     setAuth]     = useState(false);
  const [isAdmin,  setIsAdmin]  = useState(false);
  const [loading,  setLoading]  = useState(true);
  const [username, setUsername] = useState('');

  /* ───── helpers ───── */
  const setupAdminLink = useCallback(async () => {
    try {
      const { data } = await getRole();
      setIsAdmin(normIsAdmin(data));
    } catch (error) {
      console.error('Ошибка при получении роли пользователя:', error);
      setIsAdmin(false);
    }
  }, []);

  const fetchUsername = useCallback(async () => {
    try {
      const { data } = await getProfile();
      if (data?.username) {
        setUsername(data.username);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Ошибка при получении имени пользователя:', error);
      return false;
    }
  }, []);

  /* ───── первичная проверка авторизации (всегда) ───── */
  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const hasUser = await fetchUsername();
        if (!isMounted) return;

        if (hasUser) {
          setAuth(true);
          await setupAdminLink();
        } else {
          setAuth(false);
        }
      } catch {
        if (isMounted) setAuth(false);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => { isMounted = false; };
  }, [fetchUsername, setupAdminLink]);

  /* ───── публичные методы ───── */
  const login = async () => {
    // Безопасно: выставляем флаг сразу, а данные подтянем
    setAuth(true);
    await fetchUsername();
    await setupAdminLink();
  };

  const logout = async () => {
    try {
      await logoutUser();
    } catch (error) {
      console.error('Ошибка при выходе:', error);
    }
    setAuth(false);
    setIsAdmin(false);
    setUsername('');
  };

  /* ───── context value ───── */
  return (
    <AuthContext.Provider
      value={{
        auth,
        isAdmin,
        loading,
        username,
        setUsername,
        login,
        logout,
        fetchUsername,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
